import { createListenerMiddleware } from '@reduxjs/toolkit'
import { enhancedTmsApiService } from '#api/enhancements/enhancedTmsApiService'
import { wrapErrorLike } from '#features/ErrorHandling/errorHelpers'
import { toastError } from '#features/ErrorHandling/toastError'
import { type LocalConfigurationId, setApiMode, type TruckConfiguration, truckConfigurationSlice } from '#store/slices/configurationSlice'
import { logger } from '#utils/logger'
import { type AppDispatch, type RootState, store } from '../root'

interface SessionInfo {
  localConfigurationId: LocalConfigurationId
  lastApiInteraction: number
  refreshTimer?: Timer
  plannedRefreshTimestamp?: number
  retryCount: number
  isReadOnly: boolean
}

const maxRetries = 3

const sessionLogger = logger('⏲️ Configuration Session:')
export const makeKeepAliveConfigurationSessions = () => {
  const middleware = createListenerMiddleware<RootState>()
  const trackedSessions = new Map<LocalConfigurationId, SessionInfo>()

  function setConfigurationReadOnly(session: SessionInfo, dispatch: AppDispatch) {
    sessionLogger.debug('setting session to read-only', session)
    clearTimeout(session.refreshTimer)
    trackedSessions.delete(session.localConfigurationId)
    session.refreshTimer = undefined
    session.isReadOnly = true
    dispatch(setApiMode({ id: session.localConfigurationId, mode: 'read-only' }))
  }

  async function refreshSession(session: SessionInfo, dispatch: AppDispatch) {
    sessionLogger.debug('♻️ refreshing session', session)
    const remainingTime = session.plannedRefreshTimestamp ? session.plannedRefreshTimestamp - Date.now() : 0

    session.refreshTimer = window.setTimeout(
      () => {
        sessionLogger.warn(
          `☠️ Session expired ${session.localConfigurationId}. This timer should not have been triggered, if the session was refreshed correctly.`,
        )
        void setConfigurationReadOnly(session, dispatch)
      },
      Math.max(10000, remainingTime),
    )

    try {
      const { uuid } = store.getState().truckConfigurations.configurations[session.localConfigurationId] ?? {}
      if (!uuid) {
        throw new Error('No uuid for session to refresh')
      }
      const query = enhancedTmsApiService.endpoints.putApiConfigurationByUuidRefresh.initiate({
        uuid,
      })
      const request = dispatch(query)
      await request.unwrap()
      sessionLogger.debug('✅ Session refreshed', session.localConfigurationId)
      clearTimeout(session.refreshTimer)
      session.retryCount = 0
    } catch (error) {
      if (session.retryCount < maxRetries) {
        session.retryCount++
        clearTimeout(session.refreshTimer)
        sessionLogger.warn(`⚠️ Refreshing session ${session.localConfigurationId} failed, retrying (${session.retryCount}/${maxRetries})`, error)
        session.refreshTimer = setTimeout(() => {
          void refreshSession(session, dispatch)
        }, 1000 * session.retryCount)
      } else {
        toastError(wrapErrorLike(error, 'Failed to refresh configuration session'))
        if (!session.isReadOnly) {
          setConfigurationReadOnly(session, dispatch)
        }
      }
    }
  }

  middleware.startListening({
    matcher: enhancedTmsApiService.endpoints.putApiConfigurationByUuidRefresh.matchFulfilled,
    effect: (action, api) => {
      if (!enhancedTmsApiService.endpoints.putApiConfigurationByUuidRefresh.matchFulfilled(action)) {
        return
      }

      const state = api.getState().truckConfigurations
      const uuid = action.meta.arg.originalArgs.uuid
      const config = Object.values(state.configurations).find((x) => x?.uuid === uuid)
      if (!config) {
        return
      }

      const configuration: TruckConfiguration = {
        ...config,
        lastApiInteraction: action.meta.fulfilledTimeStamp as Timestamp,
      }
      api.dispatch(truckConfigurationSlice.actions._placeConfiguration({ configuration, reason: 'refreshed' }))
    },
  })

  middleware.startListening({
    predicate: (_action, currentState, previousState) => {
      return currentState.truckConfigurations.configurations !== previousState.truckConfigurations.configurations
    },
    effect: (_action, api) => {
      const configurations = api.getState().truckConfigurations.configurations
      const remainingSessions = new Set(trackedSessions.keys())

      for (const [rawConfigurationId, configuration] of Object.entries(configurations)) {
        const configurationId = rawConfigurationId as LocalConfigurationId
        // skip partial configurations
        if (!configuration?.lastApiInteraction) {
          continue
        }
        // skip loading or error states
        if (configuration.creation.error || configuration.creation.loadingStep !== 'done') {
          continue
        }

        let session = trackedSessions.get(configurationId)
        // remove the session from the remaining sessions, to keep track of the ones that are not in the current state
        remainingSessions.delete(configurationId)

        // create a new session if it doesn't exist
        if (!session) {
          const newSession: SessionInfo = {
            localConfigurationId: configuration.id,
            lastApiInteraction: 0,
            retryCount: 0,
            isReadOnly: configuration.apiConfiguration.mode === 'read-only',
          }
          trackedSessions.set(configurationId, newSession)
          session = newSession
          // console.info(`Session tracker ${configurationId} created`)
        }

        // cancel the previous timer and set a new one
        if (session.lastApiInteraction !== configuration.lastApiInteraction) {
          clearTimeout(session.refreshTimer)
          session.lastApiInteraction = configuration.lastApiInteraction

          const { sessionExpiryInMs, sessionGracePeriodInMs } = api.getState().truckConfigurations.meta

          const remainingSessionTime = sessionExpiryInMs - (Date.now() - configuration.lastApiInteraction)
          const refreshTime = Math.max(remainingSessionTime - sessionGracePeriodInMs, 0)

          if (refreshTime <= 0) {
            sessionLogger.log(`⌛ Session ${configurationId} is expired, refreshing immediately`)
            clearTimeout(session.refreshTimer)
            void refreshSession(session, api.dispatch as AppDispatch)
            continue
          }

          session.plannedRefreshTimestamp = Date.now() + refreshTime

          sessionLogger.debug(`⏳ Session ${configurationId} will be refreshed in ${(refreshTime / 1000).toFixed(1)} seconds`)
          session.refreshTimer = setTimeout(() => {
            void refreshSession(session, api.dispatch as AppDispatch)
          }, refreshTime)
        }
      }

      for (const sessionId of remainingSessions) {
        const session = trackedSessions.get(sessionId)
        if (session) {
          // console.info(`Session ${sessionId} removed, as it was not found in the current state`)
          clearTimeout(session.refreshTimer)
          trackedSessions.delete(sessionId)
        }
      }
    },
  })
  return middleware
}
